import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Info, PackageCheck, Trash2 } from 'lucide-react';
import {
  allocateHardwareRequestFulfilment,
  cancelHardwareRequestFulfilment,
  getPurchaseApproval,
  issueHardwareRequestFulfilment,
  listHardwareRequestFulfilment,
  retryHardwareRequestSourcingNote,
} from '@/api/hardwareRequests';
import { listAssets } from '@/api/assets';
import { getLocations } from '@/api/locations';
import { listHardwareRequestStockOptions } from '@/api/hardwareRequests';
import { getProjectOptions } from '@/api/projectOptions';
import { getHardwareCostProjects } from '@/api/hardwareCostProjects';
import { ApiError } from '@/api/client';
import { useAuth } from '@/auth/useAuth';
import { SupplierPicker } from '@/components/SupplierPicker';
import { formatDate, formatDateTime, formatMoneyExact } from '@/lib/dateFormat';
import { isForBranch } from '@/lib/hardwareRequest';
import type {
  Asset,
  FulfilmentKind,
  HardwareRequestDetail,
  HardwareRequestFulfilment,
  PurchaseApprovalResponse,
  PurchaseEnvelope,
} from '@/types/oscar';
import { envelopeFigures, envelopeWarning } from './envelope';

const STATUS_TONE: Record<string, string> = {
  Allocated: 'bg-lightBlue/60 text-navy',
  Issued: 'bg-green/15 text-green',
  Cancelled: 'bg-border text-darkGrey/70',
};

/**
 * Phase 4 — what is actually being GIVEN to satisfy an approved request.
 *
 * ⛔ ALLOCATION AND ISSUE ARE SEPARATE, and the panel shows them as two steps
 * rather than one button. "Allocated" is reserved; "Issued" is "it reached the
 * person". They are different days, and collapsing them would either reserve
 * nothing — two people allocate the same laptop — or mark a device deployed
 * before anyone had touched it.
 *
 * ⛔ CANCELLED ROWS STAY ON SCREEN. They are excluded from the registry's
 * counts but shown here, because "what happened to the laptop reserved last
 * week?" is exactly the question cancel exists to leave an answer to.
 */
/**
 * ⛔ THE ONLY STATES THAT MEAN "ON A SHELF SOMEWHERE". Everything else belongs
 * to somebody — deployed, reserved, lost, or away for repair — and offering one
 * of those to a hardware request is not a mistake the screen should make
 * possible.
 *
 * ⚠ Must stay in step with CK_Asset_LifecycleState (migration 066) and the
 * lifecycleStates array in pages/Assets/schema.ts.
 */
const ALLOCATABLE_STATES = [
  'Available at Branch',
  'Available at BTG',
  'Spare / Ready to Deploy',
] as const;

/**
 * One line in the asset picker.
 *
 * ⚠ Make, model, where it is and when Intune first saw it — asked for because
 * a tag and a serial alone do not tell a fulfiller whether this is the right
 * machine or whether it has been sitting unused for two years.
 */
function assetLabel(a: Asset, locationName: Map<number, string>): string {
  const makeModel = [a.Make, a.Model].filter(Boolean).join(' ');
  const where = a.LocationId != null ? locationName.get(a.LocationId) : undefined;
  const enrolled = a.IntuneEnrolledDate
    ? `enrolled ${formatDate(a.IntuneEnrolledDate)}`
    : 'not enrolled';

  return [a.AssetTag, makeModel || null, a.SerialNumber, where ?? null, enrolled]
    .filter(Boolean).join(' · ');
}

export function FulfilmentPanel({ request }: { request: HardwareRequestDetail }) {
  const { canApproveHardware, canMutate } = useAuth();
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  // ⚠ WHAT THE SERVER SAID AFTER RAISING A PURCHASE ON A CEA ENVELOPE: it went over, or the CEA is not
  // approved yet. A warning, not a failure - the purchase WAS raised - so it stays on the panel
  // rather than flashing past.
  const [envelopeNote, setEnvelopeNote] = useState<string | null>(null);

  const id = request.SubmissionId;
  const approved = request.ApprovalStatus === 'Approved';

  const fulfilment = useQuery({
    queryKey: ['hardware-request', id, 'fulfilment'],
    queryFn: ({ signal }) => listHardwareRequestFulfilment(id, signal),
  });

  const rows = fulfilment.data?.Fulfilments ?? [];
  const live = rows.filter((f) => f.Status !== 'Cancelled');

  function onSettled() {
    setError(null);
    qc.invalidateQueries({ queryKey: ['hardware-request', id] });
    qc.invalidateQueries({ queryKey: ['hardware-requests'] });
  }

  function onError(e: unknown) {
    setError(e instanceof ApiError ? e.message : 'Something went wrong. Try again.');
  }

  // ⛔ A SERIALISED STOCK ROW CANNOT BE ISSUED WITHOUT NAMING THE MACHINE
  // (076). Allocating spent a COUNT; issuing is where Oscar learns which laptop
  // actually went out. The server refuses it, and this is the screen that makes
  // that refusal impossible to walk into.
  const [issuing, setIssuing] = useState<HardwareRequestFulfilment | null>(null);

  const issue = useMutation({
    mutationFn: ({ fid, assetId }: { fid: number; assetId?: number }) =>
      issueHardwareRequestFulfilment(fid, assetId),
    onSuccess: () => { setIssuing(null); onSettled(); },
    onError,
  });

  const cancel = useMutation({
    mutationFn: ({ fid, reason }: { fid: number; reason: string }) =>
      cancelHardwareRequestFulfilment(fid, reason),
    onSuccess: onSettled,
    onError,
  });

  // ⛔ A BUTTON, NOT A BACKGROUND JOB (089). A retry loop hides a persistent
  // failure behind noise; a button somebody has pressed six times says the
  // problem is not transient and needs a person.
  //
  // ⚠ Gated on canMutate rather than canApproveHardware, to match the server.
  // Re-sending repeats what is already recorded, so it needs no more authority
  // than fulfilling the work does — and helpdesk is who notices the ticket is
  // missing the note.
  const retryNote = useMutation({
    mutationFn: (fid: number) => retryHardwareRequestSourcingNote(fid),
    onSuccess: onSettled,
    onError,
  });

  return (
    <section className="mt-4 rounded border border-border bg-white p-4 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-heading text-lg font-bold text-navy">What is being given</h2>
        {/* ⛔ NOT WHILE WE CANNOT SEE WHAT IS ALREADY ALLOCATED. Allocating
            blind is the one action that makes a failed read worse: it
            reserves the kit a second time and takes the stock count down
            twice, and the panel's own list - the thing that would show
            the duplicate - is exactly what is not loading. */}
        {canApproveHardware && approved && !adding && !fulfilment.isError && (
          <button
            type="button"
            onClick={() => { setAdding(true); setError(null); setEnvelopeNote(null); }}
            className="rounded bg-navy px-3 py-1.5 text-sm font-semibold text-white hover:bg-navy/90"
          >
            Allocate
          </button>
        )}
      </div>

      {/* ⛔ Approval first. Allocating against a pending request reserves kit
          nobody has agreed to give; against a declined one it reserves kit for
          a request that is over. The server refuses either way — this says why
          before somebody looks for the button. */}
      {!approved && (
        <p className="mt-2 flex items-start gap-2 rounded border border-border bg-bg px-3 py-2 text-xs text-darkGrey/80">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
          <span>
            {request.ApprovalStatus === 'Pending' || request.ApprovalStatus === null
              ? 'Nothing can be allocated until this request is approved.'
              : `This request was ${request.ApprovalStatus.toLowerCase()}, so nothing can be allocated to it.`}
          </span>
        </p>
      )}

      {envelopeNote && (
        <p role="status" className="mt-2 flex items-start gap-2 rounded border border-orange/40 bg-orange/10 px-3 py-2 text-xs text-darkGrey">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-orange" aria-hidden />
          <span>{envelopeNote}</span>
        </p>
      )}

      {adding && (
        <AllocateForm
          requestId={id}
          onDone={(r) => { setAdding(false); setEnvelopeNote(r.EnvelopeWarning ?? null); onSettled(); }}
          onCancel={() => setAdding(false)}
          onError={onError}
        />
      )}

      {fulfilment.isLoading && <p className="mt-3 text-sm text-darkGrey/70">Loading…</p>}

      {/* ⛔ A FAILED READ IS NOT "NOTHING ALLOCATED". Until 08/10/2026 this
          panel had no error branch: rows falls back to [] when the query
          fails, so a request whose fulfilment could not be loaded said
          "Nothing allocated yet." in the same calm voice as one that
          genuinely had none.

          ⛔ THAT IS THE MOST EXPENSIVE SENTENCE THIS PANEL CAN SAY. Anthony
          hit it with two requests whose stock had been allocated and whose
          ledger showed the CheckOut - the screen said nothing was allocated,
          and the obvious next step would have been to allocate again,
          double-reserving the kit and taking the count down twice. */}
      {fulfilment.isError && (
        <p role="alert" className="mt-3 rounded border border-orange/40 bg-orange/10 px-3 py-2 text-sm text-darkGrey">
          <strong>What is allocated could not be loaded.</strong> This is NOT the same as
          nothing being allocated — do not allocate again until it loads, or the same kit
          may be reserved twice.{' '}
          <button
            type="button"
            onClick={() => void fulfilment.refetch()}
            className="font-semibold text-navy underline"
          >
            Try again
          </button>
        </p>
      )}

      {!fulfilment.isLoading && !fulfilment.isError && rows.length === 0 && (
        <p className="mt-3 text-sm text-darkGrey/70">
          Nothing allocated yet.
        </p>
      )}

      {rows.length > 0 && (
        <ul className="mt-3 divide-y divide-border">
          {rows.map((f) => (
            <FulfilmentRow
              key={f.FulfilmentId}
              row={f}
              canAct={canApproveHardware}
              canRetryNote={canMutate}
              busy={issue.isPending || cancel.isPending}
              retrying={retryNote.isPending}
              onRetryNote={() => retryNote.mutate(f.FulfilmentId)}
              onIssue={() => {
                // Only a serialised stock row has a question to ask.
                if (f.Kind === 'Stock' && f.IsSerialisedStock) setIssuing(f);
                else issue.mutate({ fid: f.FulfilmentId });
              }}
              onCancel={(reason) => cancel.mutate({ fid: f.FulfilmentId, reason })}
            />
          ))}
        </ul>
      )}

      {issuing && (
        <IssueSerialisedDialog
          row={issuing}
          busy={issue.isPending}
          onCancel={() => setIssuing(null)}
          onIssue={(assetId) => issue.mutate({ fid: issuing.FulfilmentId, assetId })}
        />
      )}

      {/* ⛔ WAS A WARNING UNTIL 087. A branch request used to be issued without
          any assignment, because nothing mapped a branch code to an Oscar
          location — so somebody had to do it by hand. manage 067 stores the
          location the requester chose, and issuing now assigns to it.

          ⚠ Still said out loud, because "assigned to a place" is not what
          anybody expects from a screen whose other outcome is "assigned to a
          person". Informational now, not a caveat. */}
      {isForBranch(request.BeneficiaryKind) && live.length > 0 && (
        <p className="mt-3 flex items-start gap-2 rounded border border-border bg-bg px-3 py-2 text-xs text-darkGrey/80">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
          <span>
            {request.DestinationName
              ? <>This request is for a place, so issuing assigns the asset to{' '}
                <strong>{request.DestinationName}</strong> rather than to a person.</>
              : <>This request is for a place, but no Oscar location is recorded against it —
                issuing will not assign the asset to anything. Assign it by hand afterwards.</>}
          </span>
        </p>
      )}

      {error && <p className="mt-3 text-sm text-red">{error}</p>}
    </section>
  );
}

function FulfilmentRow({
  row, canAct, canRetryNote, busy, retrying, onIssue, onCancel, onRetryNote,
}: {
  row: HardwareRequestFulfilment;
  canAct: boolean;
  canRetryNote: boolean;
  busy: boolean;
  retrying: boolean;
  onIssue: () => void;
  onCancel: (reason: string) => void;
  onRetryNote: () => void;
}) {
  const [cancelling, setCancelling] = useState(false);
  const [reason, setReason] = useState('');

  return (
    <li className="py-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <span className="font-medium text-darkGrey">{row.Description}</span>
          <span className={`ml-2 rounded-full px-2 py-0.5 text-xs font-semibold ${STATUS_TONE[row.Status] ?? ''}`}>
            {row.Status}
          </span>

          {/* ⚠ NOT drawn as a problem. A missing serial is the normal state for
              stock bought in bulk — it arrives from Intune at first enrolment. */}
          {row.AssetAwaitingSerial && (
            <span className="ml-2 text-xs text-darkGrey/60">serial arrives at first enrolment</span>
          )}

          <div className="mt-1 text-xs text-darkGrey/70">
            Allocated by {row.AllocatedBy} · {formatDateTime(row.AllocatedAtUtc)}
            {row.IssuedAtUtc && <> · issued by {row.IssuedBy} · {formatDateTime(row.IssuedAtUtc)}</>}
            {row.CancelledAtUtc && <> · cancelled by {row.CancelledBy} · {formatDateTime(row.CancelledAtUtc)}</>}
          </div>

          {/* ⚠ WHAT BUDGET IT CAME OUT OF (084). Blank on a Phase 5a row where
              somebody typed a cost line number — nobody told Oscar which
              project that number belongs to, and a guess would be worse. */}
          {row.ProjectReference && (
            <div className="mt-1 text-xs text-darkGrey/70">
              Charged to {row.ProjectReference}
            </div>
          )}

          {/* ⚠ WHO IT WAS BOUGHT FROM (088). Blank on a purchase raised before
              the vendor was asked for — that means NOT RECORDED, not "no
              vendor", so it is simply absent rather than drawn as a gap. */}
          {row.VendorName && (
            <div className="mt-1 text-xs text-darkGrey/70">
              Bought from {row.VendorName}
            </div>
          )}

          {/* ⚠ WHERE THE STOCK IS (092) — the same fact the Zendesk sourcing
              note carries, shown to the person who allocated it so the panel
              and the ticket cannot tell two different stories.

              ⚠ Only ever set on a stock row: an asset or a purchase has no
              StockItemId, so the join behind it reads null and no condition on
              Kind is needed. Blank means the line has not been PLACED, which
              is a gap in the stock data rather than one in this allocation. */}
          {row.StockLocationName && (
            <div className="mt-1 text-xs text-darkGrey/70">
              Held at {row.StockLocationName}
            </div>
          )}

          {row.CancelReason && (
            <div className="mt-1 text-xs text-darkGrey/70">Reason: {row.CancelReason}</div>
          )}
          {row.Notes && <div className="mt-1 text-xs text-darkGrey/70">{row.Notes}</div>}

          <SourcingNoteState
            row={row}
            canRetry={canRetryNote}
            retrying={retrying}
            onRetry={onRetryNote}
          />
        </div>

        {canAct && row.Status !== 'Cancelled' && !cancelling && (
          <div className="flex shrink-0 gap-2">
            {row.Status === 'Allocated' && (
              <button
                type="button"
                disabled={busy}
                onClick={onIssue}
                className="inline-flex items-center gap-1 rounded border border-navy px-2 py-1 text-xs font-semibold text-navy disabled:opacity-50"
              >
                <PackageCheck size={14} aria-hidden /> Issue
              </button>
            )}
            <button
              type="button"
              disabled={busy}
              onClick={() => setCancelling(true)}
              className="inline-flex items-center gap-1 rounded border border-border px-2 py-1 text-xs font-semibold text-darkGrey disabled:opacity-50"
            >
              <Trash2 size={14} aria-hidden /> Cancel
            </button>
          </div>
        )}
      </div>

      {/* ⛔ A reason is required — the same argument the decline makes. A
          reservation released with no reason cannot be acted on. */}
      {cancelling && (
        <div className="mt-2 rounded border border-border bg-bg p-2">
          <label className="block text-xs font-semibold uppercase tracking-wide text-darkGrey/70">
            Why is this being cancelled?
            <input
              className="mt-1 w-full rounded border border-border px-2 py-1 text-sm"
              value={reason}
              maxLength={400}
              onChange={(e) => setReason(e.target.value)}
            />
          </label>
          <div className="mt-2 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => { setCancelling(false); setReason(''); }}
              className="rounded border border-border px-2 py-1 text-xs font-semibold text-darkGrey"
            >
              Keep it
            </button>
            <button
              type="button"
              disabled={reason.trim().length === 0 || busy}
              onClick={() => onCancel(reason.trim())}
              className="rounded bg-navy px-2 py-1 text-xs font-semibold text-white disabled:opacity-50"
            >
              Cancel allocation
            </button>
          </div>
        </div>
      )}
    </li>
  );
}

/**
 * Whether the SOURCING note reached the Zendesk ticket (089).
 *
 * ⛔ THREE STATES, NOT TWO. "Confirmed on the ticket", "tried and failed" and
 * "never attempted" are different facts, and a screen that collapsed the last
 * two would show every allocation made before 089 as a failure — of which there
 * are none, because nothing before 089 ever tried.
 *
 * ⚠ A failure here does NOT mean the allocation failed. The note is sent after
 * the commit — "warn, don't block" — so the kit is reserved either way. The
 * wording says so, because "Zendesk" in red next to a reservation reads as the
 * reservation having gone wrong.
 */
function SourcingNoteState({
  row, canRetry, retrying, onRetry,
}: {
  row: HardwareRequestFulfilment;
  canRetry: boolean;
  retrying: boolean;
  onRetry: () => void;
}) {
  if (row.ZendeskNoteAtUtc) {
    return (
      <div className="mt-1 text-xs text-darkGrey/60">
        Sourcing sent to the ticket as a private note · {formatDateTime(row.ZendeskNoteAtUtc)}
      </div>
    );
  }

  // ⚠ Never attempted. Silent on purpose: an allocation made before 089 has
  // nothing to report, and a line saying so on every historic row would be
  // noise that looks like a warning.
  if (!row.ZendeskNoteError) return null;

  return (
    <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-darkGrey/80">
      <span className="flex items-start gap-1">
        <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-orange" aria-hidden />
        <span>
          This allocation is recorded, but the ticket was not told how it is
          being sourced: {row.ZendeskNoteError}
        </span>
      </span>
      {canRetry && (
        <button
          type="button"
          disabled={retrying}
          onClick={onRetry}
          className="shrink-0 rounded border border-border px-2 py-1 text-xs font-semibold text-darkGrey disabled:opacity-50"
        >
          {retrying ? 'Sending…' : 'Send to ticket'}
        </button>
      )}
      {row.ZendeskNoteAttempts > 1 && (
        <span className="text-darkGrey/60">Tried {row.ZendeskNoteAttempts} times.</span>
      )}
    </div>
  );
}

/**
 * Where the IT Finance Approver's approval of this request's purchase stands, in words.
 *
 * ⚠ A READ-OUT, NOT A CONTROL. The approval is given on the request under "Needs a
 * purchase"; this only tells the allocator why the button is off, or that it is on.
 */
function PurchaseApprovalStatus({
  approval,
}: {
  approval: ReturnType<typeof useQuery<PurchaseApprovalResponse>>;
}) {
  const box = 'mt-3 rounded border px-2 py-2 text-xs text-darkGrey';

  if (approval.isError) {
    return (
      <p role="alert" className={`${box} border-orange/40 bg-orange/10`}>
        The purchase approval could not be read, so this cannot be raised yet. Try again, and if
        it keeps happening contact Onsite IT Support.
      </p>
    );
  }

  if (!approval.data) {
    return <p className={`${box} border-border bg-white`}>Checking the purchase approval…</p>;
  }

  const a = approval.data.Approval;

  if (a?.Status === 'Approved') {
    return (
      <p className={`${box} border-navy/30 bg-lightBlue/40`}>
        <strong>Approved by {a.DecidedBy}</strong>
        {a.DecidedAtUtc ? ` on ${formatDate(a.DecidedAtUtc)}` : ''}. Oscar records them as the
        approver, and says so on the Zendesk ticket (a private note).
      </p>
    );
  }

  // ⚠ One text node per state, so it reads as one sentence to a screen reader too.
  const message = a?.Status === 'Requested'
    ? `This purchase is waiting for the IT Finance Approver (asked by ${a.RequestedBy} on ${formatDate(a.RequestedAtUtc)}). `
      + 'It can be raised once they approve it.'
    : a?.Status === 'Declined'
      ? `The IT Finance Approver declined this purchase${a.DecisionNotes ? `: ${a.DecisionNotes}` : '.'}`
      : "A purchase needs the IT Finance Approver's approval first. Escalate it under 'Needs a purchase' on this request.";

  return (
    <p role="alert" className={`${box} border-orange/40 bg-orange/10`}>
      {message}
    </p>
  );
}

/**
 * Which CEA envelope this purchase draws on, where it stands now, and whether this amount fits.
 *
 * ⚠ A READ-OUT, NOT A CONTROL, and a warning rather than a block (Anthony, 29/10/2026: "If purchase
 * exceeds - warn at the moment"). Nothing here is shown when the approver said the purchase draws on
 * none.
 */
function EnvelopeStatus({
  approval,
  envelope,
  amount,
}: {
  approval: ReturnType<typeof useQuery<PurchaseApprovalResponse>>;
  envelope: PurchaseEnvelope | null;
  amount: number;
}) {
  const a = approval.data?.Approval;
  if (!a || a.ProjectForecastId === null) return null;

  const box = 'mt-2 rounded border px-2 py-2 text-xs text-darkGrey';

  // ⚠ The label is the snapshot from when it was picked; the figures are live. If they cannot be read
  // it says so rather than showing a stale copy - the server still draws the line on the right envelope.
  if (!envelope) {
    return (
      <p className={`${box} border-border bg-white`}>
        {`This draws on ${a.EnvelopeLabel ?? 'a CEA envelope'}. Its figures could not be read just now.`}
      </p>
    );
  }

  const warning = envelopeWarning(envelope, amount);

  return (
    <div className={`${box} border-border bg-white`}>
      <p>{`Draws on ${envelope.Label}: ${envelopeFigures(envelope)}.`}</p>
      {warning && (
        <p role="status" className="mt-1 flex items-start gap-1 text-darkGrey">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-orange" aria-hidden />
          <span>{warning}</span>
        </p>
      )}
    </div>
  );
}

function AllocateForm({
  requestId, onDone, onCancel, onError,
}: {
  requestId: number;
  onDone: (result: Awaited<ReturnType<typeof allocateHardwareRequestFulfilment>>) => void;
  onCancel: () => void;
  onError: (e: unknown) => void;
}) {
  const [kind, setKind] = useState<FulfilmentKind>('Asset');
  const [assetId, setAssetId] = useState<number | null>(null);
  const [stockItemId, setStockItemId] = useState<number | null>(null);
  const [qty, setQty] = useState('1');
  const [projectCostId, setProjectCostId] = useState('');
  const [amount, setAmount] = useState('');
  // ⛔ THE PREFILL MUST NOT FIGHT THE TYPIST (083). A suggested amount is
  // shown until the fulfiller touches the box; from then on what they typed
  // stands, including clearing it back to empty. Without this flag an empty
  // box is indistinguishable from an untouched one and the suggestion keeps
  // reappearing over a deliberate deletion.
  const [amountTouched, setAmountTouched] = useState(false);
  // ⚠ Raising is the default because it is the ordinary case once Phase 5b is
  // configured. Recording an existing number stays available for the day the
  // link is down, or the line was raised before anybody opened Oscar.
  const [costLineExists, setCostLineExists] = useState(false);
  const [search, setSearch] = useState('');

  // ⚠ Blank means the standing project in HardwareCostProjectReference — the
  // one that rolls over each financial year. Most purchases want it, so it is
  // the default and the picker is an override rather than a required field.
  const [projectReference, setProjectReference] = useState('');

  // ⛔ WHO IT IS BEING BOUGHT FROM. Mandatory for a capex cost line, which is
  // every line Oscar raises. Both halves are kept: the id is what travels (the
  // same SupplierId resolves in both tenants, since oscar.vSupplier and
  // project.vSupplier are one view over ref.vw_Suppliers), the name is what the
  // field shows. A free-typed name is never committed by the picker, so this
  // id always names a real creditor.
  const [vendorId, setVendorId] = useState<number | null>(null);
  const [vendorName, setVendorName] = useState('');

  // ⛔ WHEN IT IS EXPECTED TO BE PAID. A cost has no date of its own — payment
  // rows are what place it in a financial month, and without one the line sits
  // in the Capex Matrix's "Not dated" list reading as $0 in the month it was
  // committed. That is where the first live purchase landed on 02/10/2026.
  //
  // ⚠ DEFAULTED TO TODAY, NOT ASSUMED. Anthony chose to ask rather than have
  // Oscar decide, because the month this lands in is a finance answer — but a
  // default means the ordinary case is one keystroke, not a lookup.
  //
  // ⚠ Local date, not toISOString(). In Sydney the UTC date is YESTERDAY for
  // the first ten hours of every day, so toISOString would quietly file a
  // morning purchase in the previous month on the 1st.
  const [expectedPaymentDate, setExpectedPaymentDate] = useState(() => {
    const d = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  });
  const [projectSearch, setProjectSearch] = useState('');

  // ⛔ ONLY WHAT CAN ACTUALLY BE GIVEN OUT. The picker used to offer every
  // asset in the register — including machines already deployed to somebody
  // else, lost ones and those away for repair. Allocating one of those is not a
  // mistake the screen should make possible.
  //
  // ⚠ The three states are exactly the ones that mean "on a shelf somewhere":
  // at a branch, at BTG, or spare and ready. Anything else belongs to someone.
  const assets = useQuery({
    queryKey: ['assets', 'allocate', search],
    queryFn: ({ signal }) => listAssets({
      search: search || undefined,
      lifecycle: ALLOCATABLE_STATES.join(','),
      pageSize: 20,
    }, signal),
    enabled: kind === 'Asset',
  });

  // The names behind LocationId, the same way the asset register resolves them.
  const locations = useQuery({
    queryKey: ['locations', 'active'],
    queryFn: ({ signal }) => getLocations({ activeOnly: true }, signal),
    enabled: kind === 'Asset',
    retry: false,
  });
  const locationName = new Map(
    (locations.data ?? []).map((l) => [l.LocationId, l.LocationDesc] as const));

  // Which project to charge, when it is not the standing one (084).
  //
  // ⚠ AN EMPTY LIST IS NOT AN ERROR. The API returns one rather than a 500
  // when the view is missing, so the panel keeps working and the purchase goes
  // to the standing project exactly as it did before 084.
  const projects = useQuery({
    queryKey: ['project-options', projectSearch],
    queryFn: ({ signal }) => getProjectOptions(projectSearch || undefined, signal),
    enabled: kind === 'Purchase',
    retry: false,
  });

  // ⛔ WHAT "THE STANDING PROJECT" ACTUALLY IS, named on screen (085). An
  // option that says only "the standing project" is one nobody can check — and
  // the first live purchase failed against a standing project that did not
  // exist, which nothing on this screen would have revealed.
  const standing = useQuery({
    queryKey: ['hardware-cost-projects'],
    queryFn: ({ signal }) => getHardwareCostProjects(signal),
    enabled: kind === 'Purchase',
    retry: false,
  });

  const standingYear = standing.data?.CurrentFinancialYear ?? null;
  const standingProject = standingYear === null
    ? undefined
    : standing.data?.Years.find((y) => y.FinancialYear === standingYear);

  // ⛔ ONLY THE STOCK THAT CAN SATISFY THIS REQUEST (076). Listing the whole
  // warehouse made the fulfiller match spec tiers to device names in their
  // head, which is where the wrong thing gets allocated.
  // ⚠ ALSO READ FOR A PURCHASE, which has no stock to pick. The same
  // response carries what the request ASKED FOR and what the catalogue says
  // each of those costs — which is where the purchase amount comes from (083).
  const stock = useQuery({
    queryKey: ['hardware-request', requestId, 'stock-options'],
    queryFn: ({ signal }) => listHardwareRequestStockOptions(requestId, signal),
    enabled: kind === 'Stock' || kind === 'Purchase',
  });

  // ⚠ .Options, not the response itself: the same read also carries the
  // per-device summary the approver sees, which is not pickable.
  const stockOptions = stock.data?.Options ?? [];
  const chosenStock = stockOptions.find((s) => s.StockItemId === stockItemId) ?? null;

  // ⛔ A serialised line allocates ONE AT A TIME, because a fulfilment row holds
  // one asset and a row of qty 3 could never say which three machines went out.
  // The server refuses it too; this stops the quantity box offering the choice.
  const serialised = chosenStock?.IsSerialised ?? false;
  const effectiveQty = serialised ? 1 : Number(qty);

  // ⛔ ONE PRICED DEVICE, OR NO SUGGESTION AT ALL (083). A request naming
  // two different devices has no single right answer — the summary groups by
  // name, so a sum of the rows is not a sum of the units asked for, and a
  // number arrived at that way would look deliberate and be wrong. So the box
  // prefills only when there is exactly one priced device, and the prices are
  // listed instead when there is more than one.
  const pricedDevices = (stock.data?.Devices ?? []).filter((d) => d.UnitCost != null);
  const suggestedAmount = pricedDevices.length === 1 ? pricedDevices[0].UnitCost : null;

  // ⚠ DERIVED, NOT AN EFFECT. Writing the suggestion into state on load would
  // race the query and overwrite a figure already typed.
  const amountValue = amountTouched
    ? amount
    : (suggestedAmount != null ? String(suggestedAmount) : '');

  // ⛔ THE FINANCE APPROVER'S ANSWER, asked only for a Purchase. Anything but
  // Approved - loading, failed, none, waiting, declined - keeps the button off:
  // the same fail-closed the server holds.
  const approval = useQuery({
    queryKey: ['hardware-request', requestId, 'purchase-approval'],
    queryFn: ({ signal }) => getPurchaseApproval(requestId, signal),
    enabled: kind === 'Purchase',
  });
  const purchaseApproved = kind === 'Purchase' && approval.data?.Approval?.Status === 'Approved';

  // ⛔ THE ENVELOPE THE APPROVER DREW THIS ON, and so the project it is charged to: a cost line may be
  // linked to a forecast only on its own project, so the envelope's project is not a choice here. Read
  // at the point of use - a disabled query still hands back cached data.
  const envelopeRef = purchaseApproved ? (approval.data?.Approval?.EnvelopeProjectReference ?? null) : null;
  const envelope = purchaseApproved ? (approval.data?.Envelope ?? null) : null;

  const allocate = useMutation({
    mutationFn: () => allocateHardwareRequestFulfilment(requestId,
      kind === 'Asset'
        ? { Kind: 'Asset', AssetId: assetId! }
        : kind === 'Purchase'
          // ⛔ ONE OR THE OTHER, never both — the server refuses a body carrying
          // an amount AND a cost line number, because it cannot tell which the
          // person meant.
          ? (costLineExists
            ? { Kind: 'Purchase' as const, ProjectCostId: Number(projectCostId) }
            // ⛔ WHAT IS IN THE BOX, never the catalogue figure. The
            // suggestion is a default a person has seen and let stand.
            //
            // ⚠ ProjectReference is omitted when blank rather than sent as "".
            // The server falls back to the standing project on null; an empty
            // string would be a reference naming nothing.
            : {
              Kind: 'Purchase' as const,
              Amount: Number(amountValue),
              // ⚠ NEVER WITH AN ENVELOPE: the server charges the envelope's project, and naming
              // another is refused.
              ...(projectReference && !envelopeRef ? { ProjectReference: projectReference } : {}),
              // ⛔ Required by the far end for a capex line, and sent ONLY on
              // the raising path — a cost line that already exists names its
              // own vendor and the server refuses a second answer.
              VendorSupplierId: vendorId!,
              ExpectedPaymentDate: expectedPaymentDate,
              // ⛔ NO APPROVAL IS SENT. The server reads the IT Finance
              // Approver's approval of this request's purchase from the
              // database; a flag in the body could only ever say the allocator
              // agreed with themself.
            })
          : { Kind: 'Stock', StockItemId: stockItemId!, Qty: effectiveQty }),
    onSuccess: onDone,
    onError,
  });

  const ready = kind === 'Asset'
    ? assetId !== null
    : kind === 'Purchase'
      // ⛔ The cost line is the row's entire content — a purchase with no
      // number records nothing that was not already on the request.
      ? (costLineExists
        ? Number(projectCostId) > 0
        // ⚠ Amount AND a project that will resolve. An unset standing project
        // with nothing chosen is refused by the server; refusing it here means
        // the button explains itself instead of the save failing.
        // ⛔ AND A VENDOR. Capex cannot be raised without one, so refusing here
        // turns the far end's "A vendor is required for capex." into a button
        // that simply has not been satisfied yet.
        // ⛔ AND THE PURCHASE APPROVED BY THE IT FINANCE APPROVER. Raising a
        // line commits Onsite to the money; the button not being satisfied is
        // a better place to learn that than the server's refusal. Read at the
        // point of use: a disabled query still hands back cached data.
        : Number(amountValue) > 0
          && vendorId !== null
          && purchaseApproved
          && expectedPaymentDate !== ''
          && (envelopeRef !== null || projectReference !== '' || !standing.data || standingProject !== undefined))
      : stockItemId !== null && effectiveQty > 0 && (chosenStock?.QtyOnHand ?? 0) >= effectiveQty;

  return (
    <div className="mt-3 rounded border border-border bg-bg p-3">
      <fieldset>
        <legend className="mb-1 block text-xs font-semibold uppercase tracking-wide text-darkGrey/70">
          What kind
        </legend>
        <div className="flex gap-2">
          {(['Asset', 'Stock', 'Purchase'] as const).map((k) => (
            <label key={k} className="flex cursor-pointer items-center gap-2 rounded border border-border bg-white px-3 py-1.5 text-sm has-[:checked]:border-navy has-[:checked]:bg-lightBlue/40">
              <input
                type="radio"
                name="fulfilment-kind"
                value={k}
                checked={kind === k}
                onChange={() => setKind(k)}
                className="accent-navy"
              />
              {k === 'Asset'
                ? 'Choose from Available at Branch/Warehouse'
                : k === 'Stock'
                  ? 'Choose from New Stock'
                  : 'Purchase Required'}
            </label>
          ))}
        </div>
      </fieldset>

      {/* ⛔ PHASE 5a. Oscar does not create the cost line yet — that is 5b and
          it is blocked on an Entra app role. Until then the IT Asset Manager
          raises it in the Project Tracker and records its number here, which is
          still strictly better than today: an approved request that ends in a
          purchase currently leaves no trace of what paid for it. */}
      {kind === 'Purchase' ? (
        <div className="mt-3">
          {costLineExists ? (
            <label className="block text-xs font-semibold uppercase tracking-wide text-darkGrey/70">
              Project Tracker cost line
              <input
                type="number"
                className="mt-1 w-full rounded border border-border px-2 py-1 text-sm"
                value={projectCostId}
                onChange={(e) => setProjectCostId(e.target.value)}
                placeholder="e.g. 12345"
              />
            </label>
          ) : (
            <label className="block text-xs font-semibold uppercase tracking-wide text-darkGrey/70">
              What it cost (ex GST)
              <input
                type="number"
                step="0.01"
                className="mt-1 w-full rounded border border-border px-2 py-1 text-sm"
                value={amountValue}
                onChange={(e) => { setAmountTouched(true); setAmount(e.target.value); }}
                placeholder="e.g. 1299.00"
              />
            </label>
          )}

          {/* ⛔ THE SUGGESTION SAYS WHERE IT CAME FROM (083). An amount that
              appears by itself is one nobody checks; one labelled "from the
              catalogue" is one somebody either confirms or corrects. */}
          {!costLineExists && !amountTouched && suggestedAmount != null && (
            <p className="mt-1 text-xs text-darkGrey/70">
              From the catalogue: <strong>{pricedDevices[0].DeviceName}</strong> is priced at{' '}
              {formatMoneyExact(suggestedAmount)}. Change it if the quote says otherwise — what
              goes to the Project Tracker is what is in the box.
            </p>
          )}

          {/* ⚠ MORE THAN ONE PRICED DEVICE: the prices are listed and NOTHING is
              filled in. Adding them up here would be a guess at how many of each
              was asked for, and the summary groups by name so it cannot know. */}
          {!costLineExists && pricedDevices.length > 1 && (
            <p className="mt-1 text-xs text-darkGrey/70">
              This request names more than one priced device, so nothing is filled in —{' '}
              {pricedDevices.map((d) => `${d.DeviceName} ${formatMoneyExact(d.UnitCost)}`).join(', ')}.
              Enter the total being spent.
            </p>
          )}

          <label className="mt-2 flex items-center gap-2 text-xs font-normal text-darkGrey/80">
            <input
              type="checkbox"
              checked={costLineExists}
              onChange={(e) => setCostLineExists(e.target.checked)}
            />
            The cost line has already been raised in the Project Tracker
          </label>

          <p className="mt-1 text-xs text-darkGrey/60">
            {costLineExists
              ? 'Put its number here and Oscar will record it against this request.'
              : envelopeRef
                ? `Oscar raises the line against ${envelopeRef}, drawn on the CEA envelope the approver chose, and records it here.`
                : projectReference
                  ? `Oscar raises the line against ${projectReference} and records it here.`
                  : 'Oscar raises the line against the standing IT Hardware project and records it here.'}
            {' '}IT wears the cost, so it is <strong>IT&rsquo;s</strong> cost centre on that line —
            not the requester&rsquo;s.
          </p>
          {/* ⛔ WHICH BUDGET IT COMES OUT OF (084). Only when Oscar is raising
              the line — a cost line somebody has already raised is on whatever
              project they raised it on, and a picker here would let the screen
              claim otherwise.

              ⚠ Blank is the standing IT Hardware project, which is what most
              purchases want. The list is live, unarchived projects; whether one
              may actually be charged is asked again by the Project Tracker. */}
          {!costLineExists && (
            <div className="mt-3 rounded border border-border bg-white p-2">
              {envelopeRef ? (
                <p className="text-xs text-darkGrey">
                  <span className="block text-xs font-semibold uppercase tracking-wide text-darkGrey/70">Charge to</span>
                  <strong>{envelopeRef}</strong> - the project of the CEA envelope the IT Finance Approver
                  chose, so it is not a choice here.
                </p>
              ) : (<>
              <label className="block text-xs font-semibold uppercase tracking-wide text-darkGrey/70">
                Charge to
                <select
                  className="mt-1 w-full rounded border border-border px-2 py-1 text-sm"
                  value={projectReference}
                  onChange={(e) => setProjectReference(e.target.value)}
                >
                  <option value="">
                    {standingProject
                      ? `FY${standingYear} standing project — ${standingProject.ProjectReference}`
                      : 'The standing IT Hardware project'}
                  </option>
                  {(projects.data ?? []).map((p) => (
                    <option key={p.ProjectReference} value={p.ProjectReference}>
                      {p.ProjectReference} · {p.ProjectName}
                      {p.Status ? ` · ${p.Status}` : ''}
                    </option>
                  ))}
                </select>
              </label>
              <label className="mt-2 block text-xs font-semibold uppercase tracking-wide text-darkGrey/70">
                Find a project
                <input
                  className="mt-1 w-full rounded border border-border px-2 py-1 text-sm"
                  value={projectSearch}
                  onChange={(e) => setProjectSearch(e.target.value)}
                  placeholder="Reference or name"
                />
              </label>
              </>)}

              {/* ⛔ WHO IT IS BEING BOUGHT FROM. The Project Tracker refuses a
                  capex line without a vendor, so this is not optional — it is
                  asked here rather than met as a refusal after the save.

                  ⚠ The same picker the contract Vendor field uses, over the
                  same creditor master. It commits only a PICKED supplier, so
                  the id always names a real creditor rather than whatever was
                  typed. */}
              <div className="mt-2">
                <span className="block text-xs font-semibold uppercase tracking-wide text-darkGrey/70">
                  Bought from
                </span>
                <div className="mt-1">
                  <SupplierPicker
                    valueName={vendorName}
                    valueId={vendorId}
                    onChange={(name, id) => { setVendorName(name); setVendorId(id); }}
                  />
                </div>
                {vendorId === null && (
                  <p className="mt-1 text-xs text-darkGrey/60">
                    Required — a capex cost line has to name the creditor it is paid to.
                  </p>
                )}
              </div>

              {/* ⛔ WHEN IT IS EXPECTED TO BE PAID. This is what puts the cost in
                  a financial month; without it the line reads as $0 in the month
                  it was committed. Defaulted to today so the ordinary case is
                  one keystroke. */}
              <label className="mt-2 block text-xs font-semibold uppercase tracking-wide text-darkGrey/70">
                Expected payment date
                <input
                  type="date"
                  className="mt-1 w-full rounded border border-border px-2 py-1 text-sm"
                  value={expectedPaymentDate}
                  onChange={(e) => setExpectedPaymentDate(e.target.value)}
                />
              </label>
              <p className="mt-1 text-xs text-darkGrey/60">
                This is what places the cost in a financial month. Change it if the invoice
                is expected later.
              </p>

              {/* ⛔ THE SIGN-OFF IS SOMEONE ELSE'S, AND THE LAST THING BEFORE THE
                  MONEY IS COMMITTED. It used to be a tick the allocator gave
                  themself (098); it is now the IT Finance Approver's approval of
                  this request's purchase, read from the server. Nothing here can
                  grant it - it says where it stands and what to do. */}
              <PurchaseApprovalStatus approval={approval} />
              {/* ⚠ A WARNING, NEVER A BLOCK. The approver saw the headroom when they chose the
                  envelope; this is the amount actually being spent, against what is left NOW. */}
              {purchaseApproved && (
                <EnvelopeStatus
                  approval={approval}
                  envelope={envelope}
                  amount={Number(amountValue)}
                />
              )}
              {/* ⚠ Not drawn as a failure. No list means 084 has not been
                  applied yet, and the purchase still works — it goes to the
                  standing project, exactly as it did before. */}
              {projects.data && projects.data.length === 0 && (
                <p className="mt-1 text-xs text-darkGrey/60">
                  {projectSearch
                    ? 'No live project matches that.'
                    : 'No project list is available, so this will go to the standing IT Hardware project.'}
                </p>
              )}

              {/* ⛔ SAID BEFORE THE SAVE, not after the server refuses it (085).
                  With no standing project set for the year, leaving this on the
                  default is the one combination that cannot work — and the
                  fulfiller can fix it here, in the box above. */}
              {standing.data && !standingProject && projectReference === '' && !envelopeRef && (
                <p role="alert" className="mt-1 rounded border border-orange/40 bg-orange/10 px-2 py-1 text-xs text-darkGrey">
                  No standing project is set for FY{standingYear}, so you need to choose one here.
                  An admin can set the standing project on the Hardware Catalogue page.
                </p>
              )}
            </div>
          )}

          {/* ⚠ Said out loud because the alternative reading is "Oscar lost my
              laptop". A purchase names no asset by design. */}
          <p className="mt-2 rounded border border-border bg-bg px-2 py-1 text-xs text-darkGrey/80">
            This records what paid for it, not the machine. When the machine arrives, put the same
            cost line on the asset and the two find each other.
          </p>
        </div>
      ) : kind === 'Asset' ? (
        <div className="mt-3">
          <label className="block text-xs font-semibold uppercase tracking-wide text-darkGrey/70">
            Find the asset
            <input
              className="mt-1 w-full rounded border border-border px-2 py-1 text-sm"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Tag, serial or model"
            />
          </label>
          <label className="mt-2 block text-xs font-semibold uppercase tracking-wide text-darkGrey/70">
            Asset
            <select
              className="mt-1 w-full rounded border border-border px-2 py-1 text-sm"
              value={assetId ?? ''}
              onChange={(e) => setAssetId(e.target.value ? Number(e.target.value) : null)}
            >
              <option value="">Select…</option>
              {(assets.data?.Items ?? []).map((a) => (
                <option key={a.AssetId} value={a.AssetId}>
                  {assetLabel(a, locationName)}
                </option>
              ))}
            </select>
          </label>
          {/* ⚠ Worth saying: allocating does NOT move the asset's lifecycle
              state, deliberately. A reservation is not worth destroying
              "Spare / Ready to Deploy" over — the double-claim is prevented by
              a unique index instead. */}
          <p className="mt-1 text-xs text-darkGrey/60">
            Allocating reserves it without changing its lifecycle state. It cannot be allocated
            to a second request until this one is cancelled.
          </p>
        </div>
      ) : (
        <div className="mt-3 grid gap-2 sm:grid-cols-[1fr,7rem]">
          <label className="block text-xs font-semibold uppercase tracking-wide text-darkGrey/70">
            Stock item
            <select
              className="mt-1 w-full rounded border border-border px-2 py-1 text-sm"
              value={stockItemId ?? ''}
              onChange={(e) => setStockItemId(e.target.value ? Number(e.target.value) : null)}
            >
              <option value="">Select…</option>
              {stockOptions.map((s) => (
                <option key={s.StockItemId} value={s.StockItemId}>
                  {s.Name} — {s.QtyOnHand} on hand
                  {s.LocationName ? ` at ${s.LocationName}` : ''}
                  {s.ForAccessory ? ` · for accessory ${s.ForDevice}` : ` · for ${s.ForDevice}`}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-xs font-semibold uppercase tracking-wide text-darkGrey/70">
            Quantity
            <input
              type="number"
              min={1}
              disabled={serialised}
              className="mt-1 w-full rounded border border-border px-2 py-1 text-sm disabled:bg-bg disabled:text-darkGrey/60"
              value={serialised ? 1 : qty}
              onChange={(e) => setQty(e.target.value)}
            />
          </label>
          {/* ⛔ The quantity leaves availability NOW, not at issue. Otherwise
              three people allocate the same three monitors. */}
          <p className="text-xs text-darkGrey/60 sm:col-span-2">
            The quantity is checked out as soon as it is allocated, so it stops showing as
            available. Cancelling puts it back.
          </p>

          {/* ⛔ SAID BEFORE THE SAVE, not after the server refuses it. Somebody
              asking for three laptops needs to know why the box will not let
              them, and what to do instead. */}
          {serialised && (
            <p className="text-xs text-darkGrey/80 sm:col-span-2">
              <strong>Tracked by serial</strong> — allocate one at a time. Three laptops means
              three allocations, so each can be issued as its own machine.
            </p>
          )}

          {/* ⚠ Now says WHICH devices are unanswered rather than only that the
              picker is empty — the summary distinguishes "not a catalogue
              device" from "nothing mapped to it yet", and only the second is
              something the fulfiller can go and fix. */}
          {stock.data && stockOptions.length === 0 && (
            <p role="alert" className="rounded border border-orange/40 bg-orange/10 px-2 py-1 text-xs text-darkGrey sm:col-span-2">
              No stock line is mapped to what this request asked for
              {(stock.data.Devices.some((d) => d.IsInCatalogue) || (stock.data.Accessories ?? []).some((a) => a.IsInCatalogue)) && (
                <> — map one on the Stock page (<em>Satisfies…</em>)</>
              )}
              . Or allocate a specific asset instead.
            </p>
          )}

          {chosenStock && chosenStock.QtyOnHand < effectiveQty && (
            <p role="alert" className="rounded border border-red/40 bg-red/10 px-2 py-1 text-xs text-darkGrey sm:col-span-2">
              Only {chosenStock.QtyOnHand} on hand.
            </p>
          )}
        </div>
      )}

      <div className="mt-3 flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="rounded border border-border px-3 py-1.5 text-sm font-semibold text-darkGrey"
        >
          Cancel
        </button>
        <button
          type="button"
          disabled={!ready || allocate.isPending}
          onClick={() => allocate.mutate()}
          className="rounded bg-navy px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-50"
        >
          {allocate.isPending ? 'Allocating…' : 'Allocate'}
        </button>
      </div>
    </div>
  );
}

/**
 * Which machine went out (076)?
 *
 * ⛔ THE STEP THAT STOPS THE FAILURE STATE. Without it a serialised allocation
 * can be issued having spent a count and named nothing — the view calls that
 * `AwaitingAssetLink`, and it means somebody has a laptop and Oscar cannot say
 * which. So the machine is chosen here, before the issue, not chased afterwards.
 *
 * ⚠ The picker offers ANY asset with no live assignment — no lifecycle state and
 * no new flag (settled 30/09/2026). An asset nobody holds is available, which
 * deliberately allows issuing one the register still calls 'In Repair': the
 * warehouse's word beats the register's.
 */
function IssueSerialisedDialog({
  row, busy, onCancel, onIssue,
}: {
  row: HardwareRequestFulfilment;
  busy: boolean;
  onCancel: () => void;
  onIssue: (assetId: number) => void;
}) {
  const [search, setSearch] = useState('');
  const [assetId, setAssetId] = useState<number | null>(null);

  const assets = useQuery({
    queryKey: ['assets', 'issue', search],
    queryFn: ({ signal }) => listAssets({ search: search || undefined, pageSize: 20 }, signal),
  });

  return (
    <div className="mt-3 rounded border border-navy/40 bg-lightBlue/20 p-3">
      <h4 className="text-sm font-semibold text-navy">
        Which machine went out?
      </h4>
      <p className="mt-1 text-xs text-darkGrey/80">
        {row.StockItemName} was allocated as a quantity. Naming the machine links it to this
        request and assigns it to the requester.
      </p>

      <label className="mt-2 block text-xs font-semibold uppercase tracking-wide text-darkGrey/70">
        Find the asset
        <input
          className="mt-1 w-full rounded border border-border px-2 py-1 text-sm"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Tag, serial or model"
        />
      </label>

      <label className="mt-2 block text-xs font-semibold uppercase tracking-wide text-darkGrey/70">
        Asset
        <select
          className="mt-1 w-full rounded border border-border px-2 py-1 text-sm"
          value={assetId ?? ''}
          onChange={(e) => setAssetId(e.target.value ? Number(e.target.value) : null)}
        >
          <option value="">Select…</option>
          {(assets.data?.Items ?? []).map((a) => (
            <option key={a.AssetId} value={a.AssetId}>
              {[a.AssetTag, a.Make, a.Model, a.SerialNumber].filter(Boolean).join(' · ')}
            </option>
          ))}
        </select>
      </label>

      {/* ⚠ The machine is often built AFTER the request is approved, so it may
          not be in Oscar yet. Issuing can wait — the allocation holding its
          count is correct, because the stock is spoken for. */}
      <p className="mt-1 text-xs text-darkGrey/60">
        Not built yet? Leave this — the allocation holds the stock until you come back.
      </p>

      <div className="mt-3 flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="rounded border border-border px-3 py-1.5 text-sm font-semibold text-darkGrey"
        >
          Cancel
        </button>
        <button
          type="button"
          disabled={assetId === null || busy}
          onClick={() => onIssue(assetId!)}
          className="rounded bg-navy px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-50"
        >
          {busy ? 'Issuing…' : 'Issue this machine'}
        </button>
      </div>
    </div>
  );
}
